import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users,
  Pill,
  Syringe,
  Ruler,
  CalendarClock,
  ChevronRight,
  Plus,
  CalendarDays,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import ReminderBanner from "@/components/ReminderBanner";

const StatCard = ({ icon: Icon, label, value, to, accent, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group relative flex flex-col gap-3 rounded-2xl bg-card p-6 shadow-soft transition-shadow hover:shadow-card"
  >
    <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <div className="font-heading text-3xl font-extrabold leading-none">{value}</div>
      <div className="mt-1 text-sm text-muted-foreground">{label}</div>
    </div>
    <ChevronRight className="absolute right-5 top-5 h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
  </Link>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { profiles } = useOutletContext();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const loadStats = async () => {
      const todayDate = new Date().toISOString().slice(0, 10);
      const [meds, vaccs, growth, upcoming, nextAppt] = await Promise.all([
        supabase.from("medications").select("id", { count: "exact", head: true }).eq("is_active", true),
        supabase.from("vaccinations").select("id", { count: "exact", head: true }),
        supabase.from("growth_measurements").select("id", { count: "exact", head: true }),
        supabase
          .from("appointments")
          .select("id", { count: "exact", head: true })
          .gte("appointment_date", todayDate)
          .eq("status", "scheduled"),
        supabase
          .from("appointments")
          .select("*")
          .gte("appointment_date", todayDate)
          .eq("status", "scheduled")
          .order("appointment_date", { ascending: true })
          .order("appointment_time", { ascending: true })
          .limit(1)
          .maybeSingle(),
      ]);

      setStats({
        profiles: profiles?.length ?? 0,
        active_medications: meds.count ?? 0,
        vaccinations: vaccs.count ?? 0,
        growth_entries: growth.count ?? 0,
        upcoming_appointments: upcoming.count ?? 0,
        next_appointment: nextAppt.data || null,
      });
    };
    loadStats();
  }, [profiles?.length]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div className="mx-auto max-w-6xl space-y-8" data-testid="dashboard-page">
      <header>
        <span className="text-xs font-semibold uppercase tracking-wider text-accent">
          Family Hub
        </span>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
          {greeting}, {user?.name?.split(" ")[0] || "friend"}.
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Here's what's happening across your family today.
        </p>
      </header>

      <ReminderBanner />

      {profiles?.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-hero grain p-8 text-primary-foreground sm:p-12"
          data-testid="dashboard-empty-banner"
        >
          <h2 className="font-heading text-2xl font-extrabold sm:text-3xl">
            Let's set up your first family profile.
          </h2>
          <p className="mt-2 max-w-xl text-primary-foreground/80">
            Add yourself, your kids, or a parent — it takes 30 seconds and unlocks every tracker.
          </p>
          <Link to="/profiles">
            <Button
              size="lg"
              className="mt-6 bg-accent text-accent-foreground hover:bg-accent/90"
              data-testid="dashboard-empty-cta"
            >
              <Plus className="mr-1 h-4 w-4" /> Add your first profile
            </Button>
          </Link>
        </motion.div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="dashboard-stats">
            <StatCard
              icon={Users}
              label="Family profiles"
              value={stats?.profiles ?? "—"}
              to="/profiles"
              accent="bg-secondary text-primary"
              testId="stat-profiles"
            />
            <StatCard
              icon={Pill}
              label="Active meds"
              value={stats?.active_medications ?? "—"}
              to="/medications"
              accent="bg-accent/20 text-accent-foreground"
              testId="stat-medications"
            />
            <StatCard
              icon={Syringe}
              label="Vaccinations"
              value={stats?.vaccinations ?? "—"}
              to="/vaccinations"
              accent="bg-primary/10 text-primary"
              testId="stat-vaccinations"
            />
            <StatCard
              icon={Ruler}
              label="Growth entries"
              value={stats?.growth_entries ?? "—"}
              to="/growth"
              accent="bg-teal-light text-primary"
              testId="stat-growth"
            />
            <StatCard
              icon={CalendarClock}
              label="Upcoming appts"
              value={stats?.upcoming_appointments ?? "—"}
              to="/appointments"
              accent="bg-sand/30 text-foreground"
              testId="stat-appointments"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-2xl bg-card p-6 shadow-soft lg:col-span-2" data-testid="dashboard-next-appt">
              <h3 className="font-heading text-lg font-bold">Next appointment</h3>
              {stats?.next_appointment ? (
                <div className="mt-4 flex items-start gap-4 rounded-xl bg-secondary/40 p-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                    <CalendarDays className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-heading text-base font-bold">{stats.next_appointment.title}</div>
                    <div className="text-sm text-muted-foreground">
                      {stats.next_appointment.appointment_date}
                      {stats.next_appointment.appointment_time
                        ? ` at ${stats.next_appointment.appointment_time.slice(0, 5)}`
                        : ""}
                    </div>
                    {stats.next_appointment.doctor_name && (
                      <div className="text-xs text-muted-foreground">
                        with {stats.next_appointment.doctor_name}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="mt-4 text-sm text-muted-foreground">
                  Nothing scheduled.{" "}
                  <Link to="/appointments" className="text-primary hover:underline">
                    Add an appointment →
                  </Link>
                </p>
              )}
            </div>

            <div className="rounded-2xl bg-card p-6 shadow-soft" data-testid="dashboard-family">
              <h3 className="font-heading text-lg font-bold">Your family</h3>
              <ul className="mt-4 space-y-3">
                {profiles.slice(0, 4).map((p) => (
                  <li key={p.id} className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-sm font-bold text-primary-foreground">
                      {p.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold">{p.name}</div>
                      <div className="truncate text-xs capitalize text-muted-foreground">
                        {p.type}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <Link
                to="/profiles"
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
              >
                Manage profiles <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
